import { Checkbox, DatePicker, Input, Modal, Radio, RadioGroup, Select, Text } from '@viapeople/components'
import LexicalMarkdownEditor from '@viapeople/components/src/components/MarkdownEditor'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { PopulationFields } from '../../../../components/PopulationFields'
import { useToast } from '../../../../utils/ToastContext'
import { stepMetadata } from '../CycleSteps'
import { useCycleStepsStore } from '../CycleSteps/store/useCycleStepsStore'
import { type CycleStepFormData, HoldEditorEnum, RaterListApproverEnum, StepType } from '../CycleSteps/types'
import { EmailSetupSection } from './EmailSetupSection'
import { useCycleStepConfigStore } from './store'

interface ManageCycleStepProps {
	isOpen: boolean
	onClose: () => void
	cycleId: string
	organizationId: string
	stepId: string
	stepType: StepType
}

const getDefaultActionLabel = (key: string, stepType: StepType) => {
	if (stepType === StepType.ACKNOWLEDGE) {
		return key === 'submit' || key === 'approve' ? 'Acknowledge' : toSentenceCase(key)
	}
	if (stepType === StepType.RELEASE) {
		return key === 'submit' || key === 'approve' ? 'Release' : toSentenceCase(key)
	}
	if (stepType === StepType.REVIEW) {
		if (key === 'submit' || key === 'approve') return 'Approve'
		if (key === 'reject') return 'Reject'
	}

	return toSentenceCase(key)
}

const toSentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

const useCycleStepForm = (stepId?: string, stepType?: StepType) => {
	const { nodes, updateNode, diagramDefinition, edges } = useCycleStepsStore()
	const node = nodes.find((n) => n.id === stepId)
	const outgoingKeys = new Set(
		edges.filter((e) => e.source === stepId && e.data?.key).map((e) => e.data?.key as string),
	)
	// Filter once: derive the action objects whose key matches an outgoing edge.
	// Both the form's default labels (keys-only) and the modal body (full action objects)
	// derive from this same single traversal of `diagramDefinition.possibleActions`.
	const stepActions =
		(stepType && diagramDefinition?.[stepType]?.possibleActions.filter((action) => outgoingKeys.has(action.key))) || []
	const possibleActions = stepActions.map((action) => action.key)

	const defaultValues: CycleStepFormData = {
		name: node?.data.label || '',
		description: node?.data.description || '',
		actionLabels: possibleActions.reduce<Record<string, string>>((acc, actionKey) => {
			acc[actionKey] = getDefaultActionLabel(actionKey, stepType || StepType.SELF_EVALUATION)
			return acc
		}, {}),
		email: {
			kickoffTemplateId: '',
			reminderTemplateId: undefined,
			reminderDays: undefined,
			reminderLogic: undefined,
			maxReminderEmails: undefined,
		},
		isCustomParticipantsMode: false,
		participants: {
			roles: [],
			whitelistedUsers: [],
			blacklistedUsers: [],
			hierarchyLevels: [],
			blacklistedHierarchyRoles: [],
		},
		releasers: {
			roles: [],
			hierarchyLevels: [],
			blacklistedHierarchyRoles: [],
			whitelistedUsers: [],
			blacklistedUsers: [],
		},
		requireSignature: false,
		deadline: '',
		allowDenial: false,
		allowCAEdit: false,
		requireManager: '',
		includeCoManagers: false,
		coManagerResponseMode: 'independent',
		allowReviewerEdit: false,
		anonymizeUnsolicited: false,
		formId: undefined,
		holdEditors: [],
		raterListApprover: undefined,
		calculatedRole: 'rater',
	}

	const persistedActionLabels = Object.fromEntries(
		Object.entries(node?.data.options?.actionLabels ?? {}).filter(([k]) => outgoingKeys.has(k)),
	)

	const initialValues: CycleStepFormData = {
		...defaultValues,
		...(node?.data.options ?? {}),
		name: node?.data.options?.name ?? node?.data.label ?? defaultValues.name,
		description: node?.data.options?.description ?? '',
		actionLabels: {
			...defaultValues.actionLabels,
			...persistedActionLabels,
		},
	}

	const form = useForm<CycleStepFormData>({
		defaultValues: initialValues,
	})

	const updateNodeOptions = (data: CycleStepFormData) => {
		if (stepId && node) {
			updateNode(stepId, {
				data: {
					...node.data,
					label: node.data.label,
					options: {
						...node.data.options,
						...data,
						name: data.name || node.data.label,
						description: data.description || '',
						actionLabels: data.actionLabels,
					},
				},
			})
		}
	}

	return { form, updateNodeOptions, stepActions }
}

const Projected = ({ visible, children }: { visible?: boolean; children: React.ReactNode }) => {
	if (!visible) return null
	return children
}

export const ManageCycleStep: React.FC<ManageCycleStepProps> = ({
	isOpen,
	onClose,
	cycleId,
	organizationId,
	stepId,
	stepType,
}) => {
	const { showToast } = useToast()
	const { forms, reviewerGroups, fetchForms, fetchReviewerGroups } = useCycleStepConfigStore()
	useEffect(() => {
		if (isOpen) {
			if (organizationId) {
				fetchForms(organizationId)
			}
			if (cycleId) {
				fetchReviewerGroups(cycleId)
			}
		}
	}, [isOpen, organizationId, cycleId, fetchForms, fetchReviewerGroups])

	const { form, updateNodeOptions, stepActions } = useCycleStepForm(stepId, stepType)
	const { handleSubmit, watch, setValue } = form

	const includeCoManagers = watch('includeCoManagers')
	useEffect(() => {
		if (!includeCoManagers && form.getValues('coManagerResponseMode') !== undefined) {
			setValue('coManagerResponseMode', undefined, { shouldDirty: true })
		}
	}, [includeCoManagers, setValue, form])

	const groupValue = watch('group')
	useEffect(() => {
		if (!groupValue) {
			setValue('calculatedRole', 'rater', { shouldDirty: true })
		}
	}, [groupValue, setValue])

	const onSubmit = handleSubmit((data) => {
		const formData = data as CycleStepFormData

		// Validate Group Evaluation step has a reviewer group selected
		if (stepType === StepType.GROUP_EVALUATION && !formData.group) {
			showToast('Please select a reviewer group for the Group Evaluation step', 'error')
			return
		}

		if (!formData.isCustomParticipantsMode) {
			if (formData.participants) {
				formData.participants.roles = []
				formData.participants.blacklistedHierarchyRoles = []
				formData.participants.whitelistedUsers = []
				formData.participants.blacklistedUsers = []
			}
		}
		updateNodeOptions(formData)
		onClose()
	})

	const shouldFilterParticipants = watch('isCustomParticipantsMode')
	const shouldConsiderAllParticipants = !shouldFilterParticipants

	const groupOptions = reviewerGroups.map((group) => ({
		value: group.id,
		label: group.name,
	}))

	const formOptions = forms.map((f) => ({
		value: f.id,
		label: f.name,
	}))

	const step = stepMetadata[stepType]

	// Watch the deadline field to pass to EmailSetupSection
	const deadline = watch('deadline')

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			fullContent
			fullScreen
			title={step?.title}
			bodyClassName='overflow-y-auto h-full'
			buttons={[
				{
					children: 'Cancel',
					onClick: onClose,
				},
				{
					children: stepId ? 'Save Changes' : 'Create Step',
					onClick: onSubmit,
					variant: 'primary',
				},
			]}>
			<FormProvider {...form}>
				<form onSubmit={onSubmit}>
					<div className='p-8'>
						<div className='flex flex-col gap-8'>
							<Controller
								name='name'
								render={({ field: { value, onChange, ...field } }) => (
									<Input
										{...field}
										value={value ?? ''}
										label='Task name'
										placeholder='Enter task name shown to assignees'
										onChange={onChange}
									/>
								)}
							/>

							<Controller
								name='description'
								render={({ field: { value, onChange } }) => (
									<div>
										<span className='block text-sm font-medium text-neutral-700 mb-1'>Task description</span>
										<LexicalMarkdownEditor
											defaultValue={value ?? ''}
											onChangeMarkdown={onChange}
											placeholder='Enter task description shown to assignees'
										/>
									</div>
								)}
							/>

							{stepActions.length > 0 && (
								<div className='flex flex-col gap-4'>
									<div className='text-lg font-medium'>Button labels</div>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
										{stepActions.map((action) => (
											<Controller
												key={action.key}
												name={`actionLabels.${action.key}`}
												render={({ field: { value, onChange, ...field } }) => (
													<Input
														{...field}
														value={value ?? ''}
														label={`${toSentenceCase(action.key)} button text`}
														placeholder={getDefaultActionLabel(action.key, stepType)}
														onChange={onChange}
													/>
												)}
											/>
										))}
									</div>
								</div>
							)}

							{step?.group && (
								<Controller
									name='group'
									render={({ field }) => (
										<Select
											{...field}
											label='Groups'
											placeholder='Select all groups that will evaluate the participants'
											options={groupOptions}
										/>
									)}
								/>
							)}
							{step?.group && watch('group') && (
								<Controller
									name='calculatedRole'
									render={({ field }) => (
										<Select
											{...field}
											label='Task Role'
											placeholder='Select the role assigned to evaluators in this step'
											options={[
												{ value: 'rater', label: 'Rater (default)' },
												{ value: 'up', label: 'Upward' },
												{ value: 'down', label: 'Downward' },
												{ value: 'direct_reports', label: 'Direct Reports' },
											]}
										/>
									)}
								/>
							)}
							{step?.defineParticipants && (
								<>
									<Controller
										name='isCustomParticipantsMode'
										defaultValue={true}
										render={({ field: { value, onChange, ...field } }) => (
											<RadioGroup
												className='flex flex-row gap-8'
												{...field}
												value={value ? 'true' : 'false'}
												onChange={(e) => onChange(e === 'true')}>
												<Radio value='false' label='All participants' />
												<Radio value='true' label='Filter participants' />
											</RadioGroup>
										)}
									/>
									{shouldFilterParticipants && (
										<div className='flex flex-col gap-6'>
											<div className='text-lg font-medium'>{step.customParticipantsTitle || 'Filter participants'}</div>
											<PopulationFields
												name='participants'
												projection={{
													title: false,
													hierarchyTitle: false,
													hierarchyLevels: false,
													blacklistedHierarchyRoles: false,
													reviewersTitle: step.defineParticipants,
													blacklistedUsers: step.defineParticipants,
													whitelistedUsers: step.defineParticipants,
												}}
												cycleId={cycleId}
												organizationId={organizationId}
												disabled={shouldConsiderAllParticipants}
											/>
										</div>
									)}
								</>
							)}
							{step?.defineReleasers && (
								<div className='flex flex-col gap-6'>
									<div className='text-lg font-medium'>Define releasers</div>
									<PopulationFields
										name='releasers'
										projection={{
											title: false,
											hierarchyTitle: false,
											hierarchyLevels: false,
											blacklistedHierarchyRoles: false,
										}}
										cycleId={cycleId}
										organizationId={organizationId}
									/>
								</div>
							)}
							{step?.defineHoldEditors && (
								<div className='flex flex-col gap-6'>
									<div className='text-lg font-medium'>Hold Editors</div>
									<Text variant='xsmall' className='text-gray-500'>
										Select who can edit the form during the hold period. Only selected roles will receive tasks.
									</Text>
									<Controller
										name='holdEditors'
										render={({ field: { value, onChange } }) => (
											<div className='flex flex-col gap-2'>
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox
														checked={value?.includes(HoldEditorEnum.SELF) ?? false}
														onChange={(checked) => {
															const current = value || []
															onChange(
																checked
																	? [...current, HoldEditorEnum.SELF]
																	: current.filter((v: string) => v !== HoldEditorEnum.SELF),
															)
														}}
													/>
													<span>Self (Participant)</span>
												</span>
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox
														checked={value?.includes(HoldEditorEnum.MANAGER) ?? false}
														onChange={(checked) => {
															const current = value || []
															onChange(
																checked
																	? [...current, HoldEditorEnum.MANAGER]
																	: current.filter((v: string) => v !== HoldEditorEnum.MANAGER),
															)
														}}
													/>
													<span>Manager (Primary + Co-Managers)</span>
												</span>
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox
														checked={value?.includes(HoldEditorEnum.CYCLE_ADMIN) ?? false}
														onChange={(checked) => {
															const current = value || []
															onChange(
																checked
																	? [...current, HoldEditorEnum.CYCLE_ADMIN]
																	: current.filter((v: string) => v !== HoldEditorEnum.CYCLE_ADMIN),
															)
														}}
													/>
													<span>Cycle Admin</span>
												</span>
											</div>
										)}
									/>
								</div>
							)}
							{step?.defineRaterListApprover && (
								<div className='flex flex-col gap-3'>
									<div>
										<Text variant='small' weight='semibold' className='text-neutral-800'>
											Approval task assigned to
										</Text>
										<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
											Select who receives the rater list approval task for each participant.
										</Text>
									</div>
									<Controller
										name='raterListApprover'
										render={({ field: { value, onChange } }) => (
											<div className='flex flex-col gap-0 rounded-lg border border-neutral-200 overflow-hidden'>
												<button
													type='button'
													className={`flex items-start gap-3 px-4 py-3.5 w-full text-left transition-colors ${(value ?? RaterListApproverEnum.MANAGER) === RaterListApproverEnum.MANAGER ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'bg-white hover:bg-neutral-50 border-l-2 border-l-transparent'}`}
													onClick={() => onChange(RaterListApproverEnum.MANAGER)}>
													<Radio
														value={RaterListApproverEnum.MANAGER}
														checked={(value ?? RaterListApproverEnum.MANAGER) === RaterListApproverEnum.MANAGER}
														onChange={() => onChange(RaterListApproverEnum.MANAGER)}
													/>
													<div className='flex flex-col gap-0.5 -mt-0.5'>
														<Text variant='small' weight='semibold' className='text-neutral-800'>
															Direct Manager
														</Text>
														<Text variant='xsmall' className='text-neutral-500'>
															The participant's primary manager receives the approval task
														</Text>
													</div>
												</button>
												<div className='border-t border-neutral-200' />
												<button
													type='button'
													className={`flex items-start gap-3 px-4 py-3.5 w-full text-left transition-colors ${value === RaterListApproverEnum.CYCLE_ADMIN ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'bg-white hover:bg-neutral-50 border-l-2 border-l-transparent'}`}
													onClick={() => onChange(RaterListApproverEnum.CYCLE_ADMIN)}>
													<Radio
														value={RaterListApproverEnum.CYCLE_ADMIN}
														checked={value === RaterListApproverEnum.CYCLE_ADMIN}
														onChange={() => onChange(RaterListApproverEnum.CYCLE_ADMIN)}
													/>
													<div className='flex flex-col gap-0.5 -mt-0.5'>
														<Text variant='small' weight='semibold' className='text-neutral-800'>
															Cycle Admin (CA)
														</Text>
														<Text variant='xsmall' className='text-neutral-500'>
															All cycle admins receive the approval task instead of the manager
														</Text>
													</div>
												</button>
											</div>
										)}
									/>
								</div>
							)}

							{(step.showRequiredCheckboxes || step.showRequiredRadioGroup) && (
								<Text variant='small' weight='semibold' className='text-left'>
									Actions
								</Text>
							)}

							<Controller
								name='deadline'
								render={({ field: { value, onChange } }) => (
									<div>
										<span>Deadline</span>
										<DatePicker value={value} onChange={onChange} placeholder='Select date' />
									</div>
								)}
							/>

							<Controller
								name='formId'
								render={({ field }) => (
									<Select
										{...field}
										label='Form / Questionnaire'
										placeholder='Select the form/questionnaire for this step (optional)'
										options={formOptions}
									/>
								)}
							/>

							<Projected visible={step.showRequiredCheckboxes}>
								<div className='flex-1 flex flex-row gap-2 items-end'>
									<Controller
										name='requireSignature'
										render={({ field: { value, onChange } }) => (
											<span className='flex items-center gap-2 cursor-pointer'>
												<Checkbox checked={!!value} onChange={onChange} />
												<span>Require signature</span>
											</span>
										)}
									/>
									<Controller
										name='allowDenial'
										render={({ field: { value, onChange } }) => (
											<span className='flex items-center gap-2 cursor-pointer'>
												<Checkbox checked={!!value} onChange={onChange} />
												<span>Allow denial</span>
											</span>
										)}
									/>
									<Projected visible={step.showAllowCAEdit}>
										<Controller
											name='allowCAEdit'
											render={({ field: { value, onChange } }) => (
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox checked={!!value} onChange={onChange} />
													<span>Allow Admin to edit</span>
												</span>
											)}
										/>
									</Projected>
									<Projected visible={step.showReviewerEdit}>
										<Controller
											name='allowReviewerEdit'
											render={({ field: { value, onChange } }) => (
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox checked={!!value} onChange={onChange} />
													<span>Allow reviewer to edit</span>
												</span>
											)}
										/>
									</Projected>
									<Projected visible={stepType === StepType.UNSOLICITED_EVALUATION}>
										<Controller
											name='anonymizeUnsolicited'
											render={({ field: { value, onChange } }) => (
												<span className='flex items-center gap-2 cursor-pointer'>
													<Checkbox checked={!!value} onChange={onChange} />
													<span>Anonymize feedback</span>
												</span>
											)}
										/>
									</Projected>
								</div>
							</Projected>
							<Projected visible={step.showRequiredRadioGroup}>
								<div className='flex-1 flex flex-col gap-4'>
									<div className='flex-1 flex flex-row gap-2 items-end'>
										<Controller
											name='requireManager'
											render={({ field }) => (
												<RadioGroup className='flex flex-row' {...field}>
													<Radio value='primary' label='Primary manager' />
													<Radio value='secondary' label='Secondary manager' />
													<Radio value='high_level' label='High level manager' />
												</RadioGroup>
											)}
										/>
									</div>
									{watch('requireManager') === 'primary' && (
										<div className='flex flex-col gap-2'>
											<Text variant='small' weight='semibold'>
												Co-Manager Options
											</Text>
											<Controller
												name='includeCoManagers'
												render={({ field: { value, onChange } }) => (
													<span className='flex items-center gap-2 cursor-pointer'>
														<Checkbox checked={!!value} onChange={onChange} />
														<span>Include co-managers</span>
													</span>
												)}
											/>
											<Text variant='xsmall' className='text-gray-500'>
												If enabled, co-managers assigned to the participant will also receive a manager evaluation task.
											</Text>
											{watch('includeCoManagers') && (
												<div className='flex flex-col gap-2 ml-4 mt-2'>
													<Text variant='small' weight='semibold'>
														Response Mode
													</Text>
													<Controller
														name='coManagerResponseMode'
														render={({ field: { value, onChange } }) => (
															<RadioGroup
																name='coManagerResponseMode'
																className='flex flex-col gap-1'
																value={value ?? 'independent'}
																onChange={onChange}>
																<Radio value='independent' label='Independent responses' />
																<Radio value='shared' label='Shared responses' />
															</RadioGroup>
														)}
													/>
													<Text variant='xsmall' className='text-gray-500'>
														{watch('coManagerResponseMode') === 'shared'
															? 'All managers will edit the same set of responses. When any manager submits, the step is completed for all managers.'
															: 'Each manager fills out their own copy of the form independently.'}
													</Text>
												</div>
											)}
										</div>
									)}
								</div>
							</Projected>
							{step?.email && <EmailSetupSection showReminderOptions={true} deadline={deadline} />}

							{step?.emailSetup && <EmailSetupSection showReminderOptions={false} deadline={deadline} />}
						</div>
					</div>
				</form>
			</FormProvider>
		</Modal>
	)
}
